import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Mail, Calendar, ChevronRight, ArrowLeft, AlertCircle, Loader2, CheckCircle } from 'lucide-react';

import SEO from './SEO';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Newsletter Archive List View
export function NewsletterArchive() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unsubscribing, setUnsubscribing] = useState(false);
  const [unsubscribeMessage, setUnsubscribeMessage] = useState(null);

  useEffect(() => {
    fetchArchive();
  }, [token]);

  const fetchArchive = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/newsletter/${token}`);
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else if (response.status === 404) {
        setError('Invalid or expired link. Please check your email for a valid newsletter link.');
      } else {
        setError('Failed to load newsletters. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching archive:', err);
      setError('Failed to load newsletters. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    if (!window.confirm('Are you sure you want to unsubscribe from all newsletters? You can resubscribe at any time.')) {
      return;
    }

    setUnsubscribing(true);
    try {
      const response = await fetch(`${API_BASE}/api/newsletter/${token}/unsubscribe-all`, {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        setUnsubscribeMessage({ type: 'success', text: result.message });
        setData(prev => ({ ...prev, global_unsubscribed: true }));
      } else {
        setUnsubscribeMessage({ type: 'error', text: 'Failed to unsubscribe. Please try again.' });
      }
    } catch (err) {
      setUnsubscribeMessage({ type: 'error', text: 'Failed to unsubscribe. Please try again.' });
    } finally {
      setUnsubscribing(false);
    }
  };

  const handleResubscribe = async () => {
    setUnsubscribing(true);
    try {
      const response = await fetch(`${API_BASE}/api/newsletter/${token}/resubscribe`, {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        setUnsubscribeMessage({ type: 'success', text: result.message });
        setData(prev => ({ ...prev, global_unsubscribed: false }));
      } else {
        setUnsubscribeMessage({ type: 'error', text: 'Failed to resubscribe. Please try again.' });
      }
    } catch (err) {
      setUnsubscribeMessage({ type: 'error', text: 'Failed to resubscribe. Please try again.' });
    } finally {
      setUnsubscribing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Newsletters</h2>
          <p className="text-gray-600">{error}</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 text-red-600 hover:text-red-700">
            <ArrowLeft className="h-4 w-4" />
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Newsletter Archive | Owaza Martial Arts"
        description="View past newsletters from Owaza Martial Arts"
        canonical={`/newsletter/${token}`}
      />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <Mail className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Newsletter Archive</h1>
          <p className="text-gray-600">
            Newsletters for {data.recipient_email}
          </p>
        </div>

        {/* Unsubscribe Message */}
        {unsubscribeMessage && (
          <div className={`mb-6 p-4 rounded-lg ${
            unsubscribeMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {unsubscribeMessage.type === 'success' ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              {unsubscribeMessage.text}
            </div>
          </div>
        )}

        {/* Subscription Status */}
        {data.global_unsubscribed ? (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 mb-2">
              You are currently unsubscribed from all newsletters.
            </p>
            <button
              onClick={handleResubscribe}
              disabled={unsubscribing}
              className="text-yellow-700 underline hover:text-yellow-900"
            >
              {unsubscribing ? 'Processing...' : 'Click here to resubscribe'}
            </button>
          </div>
        ) : null}

        {/* Newsletter List */}
        {data.newsletters?.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No newsletters available yet.</p>
            <p className="text-sm mt-1">Check back later for updates from Owaza Martial Arts.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.newsletters?.map((newsletter) => (
              <Link
                key={newsletter.id}
                to={`/newsletter/${token}/${newsletter.id}`}
                className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-red-300 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 group-hover:text-red-600 transition-colors">
                      {newsletter.subject}
                    </h3>
                    {newsletter.preview_text && (
                      <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                        {newsletter.preview_text}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      {formatDate(newsletter.sent_at)}
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-red-600 transition-colors ml-4" />
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Unsubscribe Link */}
        {!data.global_unsubscribed && (
          <div className="mt-12 pt-6 border-t border-gray-200 text-center">
            <button
              onClick={handleUnsubscribeAll}
              disabled={unsubscribing}
              className="text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              {unsubscribing ? 'Processing...' : 'Unsubscribe from all newsletters'}
            </button>
          </div>
        )}

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-red-600">
            <ArrowLeft className="h-4 w-4" />
            Back to Owaza Martial Arts
          </Link>
        </div>
      </div>
    </>
  );
}

// Single Newsletter View
export function NewsletterView() {
  const { token, id } = useParams();
  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchNewsletter();
  }, [token, id]);

  const fetchNewsletter = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/newsletter/${token}/${id}`);
      if (response.ok) {
        const result = await response.json();
        setNewsletter(result);
      } else if (response.status === 404) {
        setError('Newsletter not found.');
      } else if (response.status === 403) {
        setError('You do not have access to this newsletter.');
      } else {
        setError('Failed to load newsletter. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching newsletter:', err);
      setError('Failed to load newsletter. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Newsletter</h2>
          <p className="text-gray-600">{error}</p>
          <Link to={`/newsletter/${token}`} className="inline-flex items-center gap-2 mt-6 text-red-600 hover:text-red-700">
            <ArrowLeft className="h-4 w-4" />
            Back to Newsletter Archive
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${newsletter.subject} | Owaza Martial Arts`}
        description={newsletter.preview_text || 'Newsletter from Owaza Martial Arts'}
        canonical={`/newsletter/${token}/${id}`}
      />

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <Link
          to={`/newsletter/${token}`}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-red-600 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Newsletter Archive
        </Link>

        {/* Newsletter Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{newsletter.subject}</h1>
          <div className="flex items-center gap-2 text-gray-500">
            <Calendar className="h-4 w-4" />
            {formatDate(newsletter.sent_at)}
          </div>
        </div>

        {/* Newsletter Content */}
        <div className="prose prose-lg max-w-none newsletter-content">
          <div dangerouslySetInnerHTML={{ __html: newsletter.content }} />
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200">
          <div className="text-center text-gray-500 text-sm">
            <p className="mb-4">
              <strong>Owaza Martial Arts</strong><br />
              14726 County Line Rd, Spring Hill, FL 34610<br />
              (352) 428-0086
            </p>
            <Link to={`/newsletter/${token}`} className="text-red-600 hover:text-red-700">
              View all newsletters
            </Link>
          </div>
        </div>
      </div>

      {/* Styles for newsletter content */}
      <style>{`
        .newsletter-content h1,
        .newsletter-content h2,
        .newsletter-content h3 {
          color: #111827;
          font-weight: 700;
          margin-top: 1.5em;
          margin-bottom: 0.75em;
        }
        .newsletter-content h1 { font-size: 1.875rem; }
        .newsletter-content h2 { font-size: 1.5rem; }
        .newsletter-content h3 { font-size: 1.25rem; }
        .newsletter-content p {
          margin-bottom: 1em;
          line-height: 1.7;
        }
        .newsletter-content ul,
        .newsletter-content ol {
          margin: 1em 0;
          padding-left: 1.5em;
        }
        .newsletter-content li {
          margin-bottom: 0.5em;
        }
        .newsletter-content a {
          color: #dc2626;
          text-decoration: underline;
        }
        .newsletter-content a:hover {
          color: #b91c1c;
        }
        .newsletter-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1em 0;
        }
        .newsletter-content blockquote {
          border-left: 4px solid #dc2626;
          padding-left: 1em;
          margin: 1em 0;
          font-style: italic;
          color: #4b5563;
        }
      `}</style>
    </>
  );
}

export default NewsletterArchive;
